/**
 * Admin Cancellation Management Page
 *
 * Dashboard for managing cancellation requests with filtering,
 * detail view, approve/deny with refund override capability.
 *
 * API Endpoints:
 *   GET  /api/v1/admin/cancellations
 *   GET  /api/v1/admin/cancellations/:id
 *   POST /api/v1/admin/cancellations/:id/approve
 *   POST /api/v1/admin/cancellations/:id/deny
 *
 * Story: NEW-004-FE - Admin Cancellation Management (UI)
 * MVP: 1.5.2 - Booking Payment Domain Model
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/shared/Modal';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'processing_refund', label: 'Processing Refund' },
  { value: 'completed', label: 'Completed' },
];

const INITIATOR_OPTIONS = [
  { value: '', label: 'All Initiators' },
  { value: 'customer', label: 'Customer' },
  { value: 'guide', label: 'Guide' },
];

const STATUS_BADGES = {
  pending: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  denied: { label: 'Denied', color: 'bg-red-100 text-red-800', icon: XCircle },
  processing_refund: { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
  completed: { label: 'Completed', color: 'bg-gray-100 text-gray-800', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: AlertCircle },
};

export default function CancellationManagement() {
  const navigate = useNavigate();
  const [cancellations, setCancellations] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [initiatorFilter, setInitiatorFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Detail modal
  const [selectedCancellation, setSelectedCancellation] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Action modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [overridePercentage, setOverridePercentage] = useState('');
  const [denyNotes, setDenyNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const fetchCancellations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (statusFilter) {params.set('status', statusFilter);}
      if (initiatorFilter) {params.set('initiatorType', initiatorFilter);}
      params.set('page', currentPage.toString());
      params.set('limit', '20');

      const response = await apiRequest(`/api/v1/admin/cancellations?${params.toString()}`);

      if (response.success) {
        setCancellations(response.data.cancellations || []);
        setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
        setStatusCounts(response.data.statusCounts || {});
      }
    } catch (err) {
      setError(err.message || 'Failed to load cancellations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, initiatorFilter, currentPage]);

  useEffect(() => {
    fetchCancellations();
  }, [fetchCancellations]);

  const fetchDetail = async (cancellationId) => {
    try {
      setDetailLoading(true);
      const response = await apiRequest(`/api/v1/admin/cancellations/${cancellationId}`);
      if (response.success) {
        setSelectedCancellation(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load cancellation details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedCancellation) {return;}

    try {
      setActionLoading(true);
      setActionError(null);

      const body = { notes: approveNotes };
      if (overridePercentage !== '') {
        body.overrideRefundPercentage = parseFloat(overridePercentage) / 100;
      }

      const response = await apiRequest(
        `/api/v1/admin/cancellations/${selectedCancellation.id}/approve`,
        { method: 'POST', body: JSON.stringify(body) }
      );

      if (response.success) {
        setShowApproveModal(false);
        setApproveNotes('');
        setOverridePercentage('');
        setSelectedCancellation(null);
        fetchCancellations();
      }
    } catch (err) {
      setActionError(err.message || 'Failed to approve cancellation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async () => {
    if (!selectedCancellation || !denyNotes.trim()) {return;}

    try {
      setActionLoading(true);
      setActionError(null);

      const response = await apiRequest(
        `/api/v1/admin/cancellations/${selectedCancellation.id}/deny`,
        { method: 'POST', body: JSON.stringify({ notes: denyNotes.trim() }) }
      );

      if (response.success) {
        setShowDenyModal(false);
        setDenyNotes('');
        setSelectedCancellation(null);
        fetchCancellations();
      }
    } catch (err) {
      setActionError(err.message || 'Failed to deny cancellation');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) {return 'N/A';}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) {return 'N/A';}
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="cancellations"
      onNavigate={(id, path) => path && navigate(path)}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Cancellation Management</h1>
          <button
            onClick={fetchCancellations}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 transition text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Status Count Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{statusCounts.pending || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Approved</p>
            <p className="text-2xl font-bold text-green-600">{statusCounts.approved || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Denied</p>
            <p className="text-2xl font-bold text-red-600">{statusCounts.denied || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Completed</p>
            <p className="text-2xl font-bold text-gray-600">{statusCounts.completed || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={initiatorFilter}
              onChange={(e) => { setInitiatorFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              {INITIATOR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
              <span className="ml-3 text-gray-600">Loading cancellations...</span>
            </div>
          ) : cancellations.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No cancellations found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Booking</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Customer</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Experience</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Refund</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Requested</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {cancellations.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-600">
                          {(c.bookingNumber || c.booking_number || c.bookingId || c.booking_id || '').toString().slice(0, 8)}
                        </span>
                        <span className="block text-xs text-gray-400 capitalize">{c.initiatorType || c.initiator_type}</span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-gray-900">{c.customerName || c.customer_name || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-gray-700 truncate max-w-[200px] block">
                          {c.experienceTitle || c.experience_title || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">{renderStatusBadge(c.status)}</td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="font-medium text-green-600">
                          {formatCurrency(c.refundToCustomerCents || c.refund_to_customer_cents)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {formatDate(c.requestedAt || c.requested_at || c.createdAt || c.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => fetchDetail(c.id)}
                          className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-dark text-xs font-medium"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage >= pagination.totalPages}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        <Modal
          isOpen={!!selectedCancellation && !showApproveModal && !showDenyModal}
          onClose={() => setSelectedCancellation(null)}
          title="Cancellation Details"
          footer={
            selectedCancellation?.status === 'pending' ? (
              <>
                <button
                  onClick={() => setSelectedCancellation(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Close
                </button>
                <button
                  onClick={() => { setShowDenyModal(true); setActionError(null); }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Deny
                </button>
                <button
                  onClick={() => { setShowApproveModal(true); setActionError(null); }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Approve
                </button>
              </>
            ) : (
              <button
                onClick={() => setSelectedCancellation(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Close
              </button>
            )
          }
        >
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : selectedCancellation ? (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-gray-500">Status</span>
                  <div className="mt-1">{renderStatusBadge(selectedCancellation.status)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Initiated By</span>
                  <p className="font-medium capitalize">{selectedCancellation.initiatorType || selectedCancellation.initiator_type}</p>
                </div>
                <div>
                  <span className="text-gray-500">Customer</span>
                  <p className="font-medium">{selectedCancellation.customerName || selectedCancellation.customer_name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Guide</span>
                  <p className="font-medium">{selectedCancellation.guideName || selectedCancellation.guide_name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Experience</span>
                  <p className="font-medium">{selectedCancellation.experienceTitle || selectedCancellation.experience_title || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Refund Amount</span>
                  <p className="font-medium text-green-600">
                    {formatCurrency(selectedCancellation.refundToCustomerCents || selectedCancellation.refund_to_customer_cents)}
                  </p>
                </div>
              </div>

              {(selectedCancellation.reason) && (
                <div>
                  <span className="text-gray-500">Reason</span>
                  <p className="mt-1 p-3 bg-gray-50 rounded-md">{selectedCancellation.reason}</p>
                </div>
              )}

              {(selectedCancellation.policyType || selectedCancellation.policy_type) && (
                <div>
                  <span className="text-gray-500">Policy Applied</span>
                  <p className="font-medium capitalize">{selectedCancellation.policyType || selectedCancellation.policy_type}</p>
                </div>
              )}

              {/* Timeline */}
              <div>
                <span className="text-gray-500">Timeline</span>
                <div className="mt-2 space-y-2">
                  {(selectedCancellation.requestedAt || selectedCancellation.requested_at) && (
                    <div className="flex items-center space-x-2 text-xs">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-600">Requested: {formatDate(selectedCancellation.requestedAt || selectedCancellation.requested_at)}</span>
                    </div>
                  )}
                  {(selectedCancellation.approvedAt || selectedCancellation.approved_at) && (
                    <div className="flex items-center space-x-2 text-xs">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span className="text-gray-600">Approved: {formatDate(selectedCancellation.approvedAt || selectedCancellation.approved_at)}</span>
                    </div>
                  )}
                  {(selectedCancellation.deniedAt || selectedCancellation.denied_at) && (
                    <div className="flex items-center space-x-2 text-xs">
                      <XCircle className="w-3 h-3 text-red-500" />
                      <span className="text-gray-600">Denied: {formatDate(selectedCancellation.deniedAt || selectedCancellation.denied_at)}</span>
                    </div>
                  )}
                  {(selectedCancellation.completedAt || selectedCancellation.completed_at) && (
                    <div className="flex items-center space-x-2 text-xs">
                      <CheckCircle className="w-3 h-3 text-brand-primary" />
                      <span className="text-gray-600">Completed: {formatDate(selectedCancellation.completedAt || selectedCancellation.completed_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </Modal>

        {/* Approve Modal */}
        <Modal
          isOpen={showApproveModal}
          onClose={() => { setShowApproveModal(false); setActionError(null); }}
          title="Approve Cancellation"
          footer={
            <>
              <button
                onClick={() => { setShowApproveModal(false); setActionError(null); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
              >
                {actionLoading ? 'Approving...' : 'Approve Cancellation'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            {selectedCancellation && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p>Refund: <span className="font-medium text-green-600">{formatCurrency(selectedCancellation.refundToCustomerCents || selectedCancellation.refund_to_customer_cents)}</span></p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Override Refund Percentage (optional)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={overridePercentage}
                  onChange={(e) => setOverridePercentage(e.target.value)}
                  placeholder="e.g. 75"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Leave blank to use policy-calculated refund</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder="Add notes about this approval..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
              />
            </div>

            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{actionError}</p>
              </div>
            )}
          </div>
        </Modal>

        {/* Deny Modal */}
        <Modal
          isOpen={showDenyModal}
          onClose={() => { setShowDenyModal(false); setActionError(null); }}
          title="Deny Cancellation"
          footer={
            <>
              <button
                onClick={() => { setShowDenyModal(false); setActionError(null); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeny}
                disabled={actionLoading || !denyNotes.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition"
              >
                {actionLoading ? 'Denying...' : 'Deny Cancellation'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Denial <span className="text-red-500">*</span>
              </label>
              <textarea
                value={denyNotes}
                onChange={(e) => setDenyNotes(e.target.value)}
                placeholder="Explain why this cancellation is being denied..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
              />
              {!denyNotes.trim() && (
                <p className="text-xs text-red-500 mt-1">Notes are required to deny a cancellation</p>
              )}
            </div>

            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{actionError}</p>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
}
